import { useEffect, useState } from "react";
import tw from "tailwind-styled-components";
import axios from "axios";
import { useMutation } from "react-query";
import { useSelector } from "react-redux";
import { MdClose } from "react-icons/md";
import { FaUserCircle, FaCheckCircle } from "react-icons/fa";
import { FiChevronDown } from "react-icons/fi";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import Config from "../../Config";
import { handleCopy } from "../../helpers";
import { ModalHeader, ScrollableContent } from "../Styles/InputStyles";
import { IoCopySharp } from "react-icons/io5";
import NotFoundModel from "../NotFoundModel";

const formatCurrency = (value) =>
    new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
    }).format(Number(value) || 0);

const isKycVerified = (status) =>
    Number(status) === 4 || Number(status) === 5;

const getTenantSummary = (tenant) => {
    const dues = tenant?.dues ?? [];

    const totalBalance = dues.reduce(
        (sum, d) => sum + (Number(d.balance) || 0),
        0
    );

    const today = new Date();

    let oldestOverdueDays = 0;
    let oldestDueDate = null;

    dues.forEach((d) => {
        if ((Number(d.balance) || 0) <= 0) return;
        const dt = new Date(d.dueDate);
        if (isNaN(dt.getTime())) return;

        const diffDays = Math.floor(
            (today - dt) / (1000 * 60 * 60 * 24)
        );
        if (diffDays > oldestOverdueDays) oldestOverdueDays = diffDays;
        if (!oldestDueDate || dt < oldestDueDate) oldestDueDate = dt;
    });

    return {
        totalBalance,
        overdueDays: oldestOverdueDays,
        dueSince: oldestDueDate
            ? oldestDueDate.toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            })
            : "",
        unpaidCount: dues.filter((d) => (Number(d.balance) || 0) > 0).length,
    };
};

const getDueTypeLabel = (type) => {
    if (!Config?.dueType) return "Other";
    const entry = Object.entries(Config.dueType).find(
        ([_, v]) => v === type
    );
    if (!entry) return "Other";
    return entry[0].replace(/_/g, " ");
};

const RentDefaultersModal = ({ isOpen, onClose, propId }) => {
    const user = useSelector((state) => state.UserReducer.user);
    const [defaulters, setDefaulters] = useState([]);

    const fetchFunction = async () => {
        const response = await axios.get(
            `${Config.apiUrl}/due/rent/defaulters/list?propId=${propId}`,
            {
                headers: {
                    authorization: "Bearer" + " " + user.token,
                    "Content-Type":
                        "application/x-www-form-urlencoded; charset=UTF-8",
                },
            }
        );
        return response.data;
    };

    const getSuccess = (res) => {
        if (res.data === false) {
            setDefaulters([]);
        } else {
            setDefaulters(Array.isArray(res?.data) ? res.data : []);
        }
    };

    const getError = (err) => {
        toast.error(
            err?.response?.data?.msg || "Failed to load rent defaulters"
        );
    };

    const {
        isLoading,
        mutate: getListMutate,
        error,
        isRefetching,
    } = useMutation(fetchFunction, {
        onSuccess: getSuccess,
        onError: getError,
    });

    useEffect(() => {
        // if (isOpen && propId) {
        if (isOpen && propId !== undefined && propId !== null) {
            getListMutate();
        }
    }, [isOpen, propId]);

    return (
        <Container>
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">
                    Rent Defaulters
                </h1>
                <button onClick={onClose} type="button">
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>
            {isLoading || isRefetching ? (
                <div className="flex h-[calc(100vh-80px)] w-full items-center justify-center">
                    <CircularProgress size={32} className="text-primary-color" />
                </div>
            ) : (
                <ScrollableContent className="max-h-[calc(100vh-80px)]">
                    <div className="px-4 py-3">
                        {error ? (
                            <div className="flex h-[calc(100vh-80px)] w-full items-center justify-center">
                                {/* <EmptyState message="Failed to load defaulters." /> */}
                                <NotFoundModel />
                            </div>
                        ) : defaulters.length === 0 ? (
                            <EmptyState message="No rent defaulters found for this property." />
                        ) : (
                            <div className="space-y-3">
                                {defaulters.map((tenant) => (
                                    <DefaulterCard
                                        key={tenant.tenantId}
                                        tenant={tenant}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </ScrollableContent>
            )}
        </Container>
    );
};


const DefaulterCard = ({ tenant }) => {
    const [expanded, setExpanded] = useState(false);
    const name = tenant.tenantName ?? "Unknown Tenant";
    const phone = tenant.tenantMobile ?? "";
    const profilePicture = tenant.profilePicture ?? "";
    const kycStatus = tenant.kycStatus;
    const dues = tenant.dues ?? [];
    const summary = getTenantSummary(tenant);
    const hasUnpaid = summary.unpaidCount > 0;
    const verified = isKycVerified(kycStatus);

    return (
        <Card $open={expanded}>
            <HeaderButton
                type="button"
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
            >
                <div className="relative h-10 w-10 shrink-0">
                    <AvatarBox>
                        {profilePicture ? (
                            <img
                                src={profilePicture}
                                alt={name}
                                className="h-full w-full object-cover"
                            />
                        ) : (
                            <FaUserCircle
                                size="100%"
                                className="text-gray-300"
                            />
                        )}
                    </AvatarBox>

                    {verified && (
                        <VerifiedBadge title="KYC Verified">
                            <FaCheckCircle size={10} />
                        </VerifiedBadge>
                    )}
                </div>

                <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-gray-800">
                        {name}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-400 flex items-center gap-1">
                        {/* {hasUnpaid
                            ? `${summary.unpaidCount} unpaid due${
                                  summary.unpaidCount > 1 ? "s" : ""
                              }`
                            : "No pending dues"} */}
                        <span>{'#' + phone}</span>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCopy(phone);
                            }}
                            className="hover:text-gray-600 transition"
                            title="Copy number"
                        >
                            <IoCopySharp size={12} />
                        </button>
                    </p>
                </div>

                <div className="flex shrink-0 flex-col items-end">
                    <p className="text-base font-semibold text-gray-800 tabular-nums">
                        {formatCurrency(summary.totalBalance)}
                    </p>
                    {summary.overdueDays > 0 && (
                        <p className="mt-0.5 text-[11px] text-gray-500">
                            {summary.overdueDays} day
                            {summary.overdueDays > 1 ? "s" : ""} overdue
                        </p>
                    )}
                </div>

                <ChevronWrap $open={expanded}>
                    <FiChevronDown size={18} />
                </ChevronWrap>
            </HeaderButton>

            <AccordionWrapper $open={expanded}>
                <AccordionInner>
                    {dues.length > 0 && (
                        <BreakdownBlock>
                            <BreakdownTitle>
                                Dues breakdown
                            </BreakdownTitle>

                            <BreakdownScroll>
                                <div className="space-y-2">
                                    {dues
                                        .filter(
                                            (d) =>
                                                (Number(d.balance) || 0) > 0
                                        )
                                        .map((due) => (
                                            <DueRow
                                                key={due.id}
                                                due={due}
                                            />
                                        ))}
                                </div>
                            </BreakdownScroll>
                        </BreakdownBlock>
                    )}
                </AccordionInner>
            </AccordionWrapper>
        </Card>
    );
};

const DueRow = ({ due }) => {
    const label = due.title || getDueTypeLabel(due.type);
    const dueDate = due.dueDate
        ? new Date(due.dueDate).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        })
        : "-";

    return (
        <DueItem>
            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-gray-700">
                    {label}
                </p>
                <p className="mt-0.5 text-xs text-gray-400">
                    {dueDate}
                </p>
            </div>
            <p className="shrink-0 text-sm font-bold text-rose-600 tabular-nums">
                {formatCurrency(due.balance ?? due.amount ?? 0)}
            </p>
        </DueItem>
    );
};

const EmptyState = ({ message }) => (
    <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/60 py-10 px-4 text-center">
        <p className="text-sm text-gray-500">{message}</p>
    </div>
);


const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const Card = tw.div`
    rounded-lg border border-gray-200 bg-white overflow-hidden
    shadow-sm transition-all duration-200
    ${(p) => (p.$open ? "shadow-md border-gray-300" : "")}
`;
const HeaderButton = tw.button`
    flex items-center gap-3 w-full p-3 text-left
    transition-colors hover:bg-gray-50
`;
const AvatarBox = tw.div`
    h-10 w-10 overflow-hidden rounded-full bg-gray-100
    flex items-center justify-center
`;
const VerifiedBadge = tw.span`
    absolute bottom-1 -right-0.5
    flex items-center justify-center
    rounded-full text-green-500
    shadow-md ring-2 ring-white
`;
const ChevronWrap = tw.span`
    flex h-7 w-7 shrink-0 items-center justify-center
    rounded-full text-gray-400 transition-transform duration-200
    ${(p) => (p.$open ? "rotate-180 text-gray-600" : "")}
`;
const AccordionWrapper = tw.div`
    overflow-hidden
    transition-[max-height,opacity] duration-300 ease-in-out
    ${(p) => (p.$open ? "max-h-[700px] opacity-100" : "max-h-0 opacity-0")}
`;
const AccordionInner = tw.div`
    border-t border-gray-100 px-3 pb-3 pt-3 space-y-3
`;
const BreakdownBlock = tw.div`
    rounded-lg bg-gray-50 border border-gray-100 p-3 space-y-2
`;
const BreakdownTitle = tw.p`
    text-xs font-bold uppercase tracking-wider text-gray-400
`;
const BreakdownScroll = tw.div`
    max-h-72 overflow-y-auto pr-1 overscroll-contain
`;
const DueItem = tw.div`
    flex items-center justify-between gap-2
    rounded-md bg-white px-3 py-2
    border border-gray-100
`;

export default RentDefaultersModal;